import React,{useState,useEffect,useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Header from './Header';
import LineScore from './LineScore';
import GameSummary from './GameSummary';
import PlayerStats from './PlayerStats';
import Standings from './Standings';
import ScheduleCalendar from './ScheduleCalendar';
import RosterDisplay from './RosterDisplay';
import SeasonOverview from './SeasonOverview';
import TeamPerformanceDashboard from './dashboard/TeamPerformanceDashboard';
import PlayerPerformanceDashboard from './playerDashboard/PlayerPerformanceDashboard';
import InGameHitTracker from './hitTracker/InGameHitTracker';
import GameCenterPanel from './gameCenter/GameCenterPanel';
import InjuryTracker from './InjuryTracker';
import SeasonHighlights from './season/SeasonHighlights';
import FAQSection from './FAQSection';
import Footer from './Footer';
import {fetchRedSoxData} from '../api/mlb';
import {fetchGameAnalysis} from '../api/gameAnalysis';
import {fetchGameRecap} from '../api/gameRecap';
import {saveCompletedGame,getStoredGames} from '../api/gameHistory';

const {FiAward}=FiIcons;
const LIVE_INTERVAL=10000;
const DEFAULT_INTERVAL=30000;

const GreenMonster=()=> {
  const navigate=useNavigate();
  const [data,setData]=useState({
    currentGame: null,
    schedule: [],
    standings: [],
    roster: [],
  });
  const [loading,setLoading]=useState(true);
  const [lastUpdated,setLastUpdated]=useState(null);
  const [error,setError]=useState(null);
  const savedGamesRef=useRef(new Set(getStoredGames().map((g)=> g.gamePk)));
  const intervalRef=useRef(null);

  const tryAutoSaveGame=async (game)=> {
    if (!game?.gamePk) return;
    if (game?.status?.abstractGameState !=='Final') return;
    if (savedGamesRef.current.has(game.gamePk)) return;

    try {
      const [analysis,recap]=await Promise.all([
        fetchGameAnalysis(game.gamePk),
        fetchGameRecap(game.gamePk),
      ]);
      saveCompletedGame(game,analysis,recap);
      savedGamesRef.current.add(game.gamePk);
    } catch (err) {
      console.error('Auto-save failed:',err);
    }
  };

  const loadData=async ()=> {
    try {
      const result=await fetchRedSoxData();
      setData(result);
      setLastUpdated(new Date());
      setError(null);
      if (result.currentGame) {
        tryAutoSaveGame(result.currentGame);
      }
    } catch (err) {
      setError('Could not load MLB data. Check connection or API status.');
    } finally {
      setLoading(false);
    }
  };

  const startInterval=(isLive)=> {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const delay=isLive ? LIVE_INTERVAL : DEFAULT_INTERVAL;
    intervalRef.current=setInterval(loadData,delay);
  };

  useEffect(()=> {
    loadData();
    startInterval(false);
    return ()=> {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  },[]);

  useEffect(()=> {
    const isLive=data.currentGame?.status?.abstractGameState==='Live';
    startInterval(isLive);
  },[data.currentGame?.status?.abstractGameState]);

  const gameState=data.currentGame?.status?.abstractGameState;
  const showGameCenter=gameState==='Live' || gameState==='Final';

  return (
    <div className="min-h-screen flex flex-col items-center justify-start p-2 sm:p-4 md:p-8 lg:p-12 bg-[#1e3a29]">
      <div className="w-full max-w-[1500px] border-[8px] sm:border-[12px] md:border-[16px] border-[#d8ceb3] rounded-xl p-1 bg-[#386646] shadow-2xl">
        <div className="border border-[#8aa890] rounded-md p-3 sm:p-6 md:p-8 lg:p-10 flex flex-col h-full gap-4 sm:gap-8">
          <div className="flex justify-end mb-[-1rem] sm:mb-[-2rem] relative z-10">
            <button
              onClick={()=> navigate('/franchise')}
              className="flex items-center gap-1.5 bg-[#d8ceb3] text-[#1e3a29] hover:bg-white text-[10px] sm:text-xs font-black uppercase tracking-widest px-3 py-1.5 sm:px-4 sm:py-2 shadow-lg transition-colors border-2 border-[#1e3a29]"
            >
              <SafeIcon icon={FiAward} size={14} />
              Franchise History
            </button>
          </div>

          <Header loading={loading} error={error} game={data.currentGame} />

          <div className="flex flex-col gap-6 sm:gap-10">
            <div className="flex flex-col gap-4 sm:gap-6">
              <LineScore game={data.currentGame} />
              <GameSummary game={data.currentGame} lastUpdated={lastUpdated} />
              {showGameCenter && <GameCenterPanel game={data.currentGame} />}
              {showGameCenter && <InGameHitTracker game={data.currentGame} />}
            </div>

            <SeasonHighlights games={data.schedule} standings={data.standings} />
            <SeasonOverview standings={data.standings} games={data.schedule} />

            <div className="flex flex-col gap-6">
              <TeamPerformanceDashboard standings={data.standings} />
              <PlayerPerformanceDashboard />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <InjuryTracker />
              <Standings standings={data.standings} />
            </div>

            <div className="flex flex-col gap-6">
              <PlayerStats game={data.currentGame} />
              <ScheduleCalendar games={data.schedule} />
              <RosterDisplay roster={data.roster} />
            </div>

            <FAQSection />
          </div>

          <Footer error={error} />
        </div>
      </div>
    </div>
  );
};

export default GreenMonster;